import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { MailWarning, X } from 'lucide-react';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { getErrorMessage } from '../../lib/apiClient';
import { requestEmailVerification } from './authApi';

/**
 * Shown on every authenticated page until the user verifies their email.
 * Dismissible for the session; the resend button respects the backend throttle.
 */
export default function VerificationBanner() {
  const { data: user } = useCurrentUser();
  const [dismissed, setDismissed] = useState(false);

  const resend = useMutation({
    mutationFn: requestEmailVerification,
    onSuccess: () => toast.success('Verification email sent'),
    onError: (err) => toast.error(getErrorMessage(err, 'Could not send verification email')),
  });

  if (!user || user.emailVerifiedAt || dismissed) return null;

  return (
    <div className="mb-4 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
      <MailWarning className="h-4 w-4 shrink-0" />
      <span className="flex-1">
        Please verify your email address to secure your account.
      </span>
      <button
        onClick={() => resend.mutate()}
        disabled={resend.isPending}
        className="font-medium text-amber-900 underline hover:no-underline disabled:opacity-50"
      >
        Resend email
      </button>
      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="text-amber-500 hover:text-amber-700"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
